/**
 * TrackOtic API Service
 * Centralized API calls for v1 endpoints.
 *
 * Auth: HTTPOnly cookie (primary) + module-level Authorization header
 * managed by TrackOticAuthContext. No localStorage tokens here.
 */
import axios from 'axios';

const API_BASE = process.env.REACT_APP_BACKEND_URL;

// Send the HTTPOnly auth cookie on every cross-origin call.
axios.defaults.withCredentials = true;

// Control Portal APIs (Track0tic Admin)
export const controlApi = {
  // Dashboard
  getDashboardStats: () => axios.get(`${API_BASE}/api/v1/control/dashboard/stats`),
  getRecentAlerts: (params) => axios.get(`${API_BASE}/api/v1/control/dashboard/recent-alerts`, { params }),
  getExpiringLicenses: (params) => axios.get(`${API_BASE}/api/v1/control/dashboard/expiring-licenses`, { params }),
  getDeviceLocations: (params) => axios.get(`${API_BASE}/api/v1/control/dashboard/device-locations`, { params }),
  getCustomerSummary: () => axios.get(`${API_BASE}/api/v1/control/dashboard/customer-summary`),

  // Tenants
  getTenants: (params) => axios.get(`${API_BASE}/api/v1/control/tenants`, { params }),
  createTenant: (data) => axios.post(`${API_BASE}/api/v1/control/tenants`, data),
  getTenant: (id) => axios.get(`${API_BASE}/api/v1/control/tenants/${id}`),
  updateTenant: (id, data) => axios.put(`${API_BASE}/api/v1/control/tenants/${id}`, data),
  getTenantStats: (id) => axios.get(`${API_BASE}/api/v1/control/tenants/${id}/stats`),

  // Locations
  getLocations: (tenantId, params) => axios.get(`${API_BASE}/api/v1/control/tenants/${tenantId}/locations`, { params }),
  createLocation: (tenantId, data) => axios.post(`${API_BASE}/api/v1/control/tenants/${tenantId}/locations`, data),
  importLocations: (tenantId, file) => {
    const formData = new FormData();
    formData.append('file', file);
    return axios.post(`${API_BASE}/api/v1/control/tenants/${tenantId}/locations/import`, formData, {
      headers: { 'Content-Type': 'multipart/form-data' }
    });
  },
  updateLocation: (tenantId, locationId, data) => axios.put(`${API_BASE}/api/v1/control/tenants/${tenantId}/locations/${locationId}`, data),
  moveLocation: (tenantId, locationId, newParentId) => axios.post(`${API_BASE}/api/v1/control/tenants/${tenantId}/locations/${locationId}/move`, null, { params: { new_parent_id: newParentId } }),

  // Users
  getUsers: (tenantId, params) => axios.get(`${API_BASE}/api/v1/control/tenants/${tenantId}/users`, { params }),
  createUser: (tenantId, data) => axios.post(`${API_BASE}/api/v1/control/tenants/${tenantId}/users`, data),
  updateUser: (tenantId, userId, data) => axios.put(`${API_BASE}/api/v1/control/tenants/${tenantId}/users/${userId}`, data),
  getUserRoles: (tenantId, userId) => axios.get(`${API_BASE}/api/v1/control/tenants/${tenantId}/users/${userId}/roles`),
  assignUserRoles: (tenantId, userId, assignments) => axios.put(`${API_BASE}/api/v1/control/tenants/${tenantId}/users/${userId}/roles`, { assignments }),

  // Roles
  getRoles: (tenantId) => axios.get(`${API_BASE}/api/v1/control/tenants/${tenantId}/roles`),
  createRole: (tenantId, data) => axios.post(`${API_BASE}/api/v1/control/tenants/${tenantId}/roles`, data),
  updateRole: (tenantId, roleId, data) => axios.put(`${API_BASE}/api/v1/control/tenants/${tenantId}/roles/${roleId}`, data),
  setRolePermissions: (tenantId, roleId, permissionCodes) => axios.put(`${API_BASE}/api/v1/control/tenants/${tenantId}/roles/${roleId}/permissions`, { permission_codes: permissionCodes }),

  // Permissions
  getPermissions: () => axios.get(`${API_BASE}/api/v1/control/permissions`),

  // Devices
  getDevices: (tenantId, params) => axios.get(`${API_BASE}/api/v1/control/tenants/${tenantId}/devices`, { params }),
  createDevice: (tenantId, data) => axios.post(`${API_BASE}/api/v1/control/tenants/${tenantId}/devices`, data),
  updateDevice: (tenantId, deviceId, data) => axios.put(`${API_BASE}/api/v1/control/tenants/${tenantId}/devices/${deviceId}`, data),
  setDeviceEntitlement: (tenantId, deviceId, data) => axios.put(`${API_BASE}/api/v1/control/tenants/${tenantId}/devices/${deviceId}/entitlement`, data),
  suspendDevice: (tenantId, deviceId) => axios.post(`${API_BASE}/api/v1/control/tenants/${tenantId}/devices/${deviceId}/suspend`),
  resumeDevice: (tenantId, deviceId) => axios.post(`${API_BASE}/api/v1/control/tenants/${tenantId}/devices/${deviceId}/resume`),
  revokeDevice: (tenantId, deviceId) => axios.post(`${API_BASE}/api/v1/control/tenants/${tenantId}/devices/${deviceId}/revoke`),

  // Notifications
  getNotificationEndpoints: (tenantId) => axios.get(`${API_BASE}/api/v1/control/tenants/${tenantId}/notification-endpoints`),
  createNotificationEndpoint: (tenantId, data) => axios.post(`${API_BASE}/api/v1/control/tenants/${tenantId}/notification-endpoints`, data),
  updateNotificationEndpoint: (tenantId, endpointId, data) => axios.put(`${API_BASE}/api/v1/control/tenants/${tenantId}/notification-endpoints/${endpointId}`, data),

  // Alerts (Global)
  getGlobalAlerts: (params) => axios.get(`${API_BASE}/api/v1/control/alerts`, { params }),
  getAlertStats: (params) => axios.get(`${API_BASE}/api/v1/control/alerts/stats`, { params }),
  getAlertDetail: (alertId) => axios.get(`${API_BASE}/api/v1/control/alerts/${alertId}`),
  acknowledgeAlert: (alertId, data) => axios.post(`${API_BASE}/api/v1/control/alerts/${alertId}/acknowledge`, data),
  escalateAlert: (alertId, data) => axios.post(`${API_BASE}/api/v1/control/alerts/${alertId}/escalate`, data),
  resolveAlert: (alertId, data) => axios.post(`${API_BASE}/api/v1/control/alerts/${alertId}/resolve`, data),
  addAlertComment: (alertId, data) => axios.post(`${API_BASE}/api/v1/control/alerts/${alertId}/comment`, data),

  // Fleet & Asset Management
  getFleetSummary: (params) => axios.get(`${API_BASE}/api/v1/control/fleet/summary`, { params }),
  getVehicles: (params) => axios.get(`${API_BASE}/api/v1/control/fleet/vehicles`, { params }),
  createVehicle: (tenantId, data) => axios.post(`${API_BASE}/api/v1/control/fleet/vehicles/${tenantId}`, data),
  updateVehicleStatus: (vehicleId, status) => axios.patch(`${API_BASE}/api/v1/control/fleet/vehicles/${vehicleId}/status`, null, { params: { status } }),
  getDrivers: (params) => axios.get(`${API_BASE}/api/v1/control/fleet/drivers`, { params }),
  createDriver: (tenantId, data) => axios.post(`${API_BASE}/api/v1/control/fleet/drivers/${tenantId}`, data),
  getAssets: (params) => axios.get(`${API_BASE}/api/v1/control/fleet/assets`, { params }),
  createAsset: (tenantId, data) => axios.post(`${API_BASE}/api/v1/control/fleet/assets/${tenantId}`, data),

  // Shipment & Trip Management
  getShipmentSummary: (params) => axios.get(`${API_BASE}/api/v1/control/shipments/summary`, { params }),
  getShipments: (params) => axios.get(`${API_BASE}/api/v1/control/shipments`, { params }),
  getShipmentDetail: (tripId) => axios.get(`${API_BASE}/api/v1/control/shipments/${tripId}`),
  getShipmentsByCustomer: () => axios.get(`${API_BASE}/api/v1/control/shipments/by-customer`),

  // Cold Chain Monitoring
  getColdChainDashboard: (params) => axios.get(`${API_BASE}/api/v1/control/cold-chain/dashboard`, { params }),
  getColdChainDevices: (params) => axios.get(`${API_BASE}/api/v1/control/cold-chain/devices`, { params }),

  // Device Registry & Dispatch
  getDeviceRegistryStats: () => axios.get(`${API_BASE}/api/v1/control/device-registry/stats`),
  getRegistryDevices: (params) => axios.get(`${API_BASE}/api/v1/control/device-registry/devices`, { params }),
  registerDevice: (data) => axios.post(`${API_BASE}/api/v1/control/device-registry/devices`, data),
  getRegistryDeviceDetail: (registryId) => axios.get(`${API_BASE}/api/v1/control/device-registry/devices/${registryId}`),
  submitQCChecklist: (registryId, data) => axios.post(`${API_BASE}/api/v1/control/device-registry/devices/${registryId}/qc`, data),
  startFOTA: (registryId, data) => axios.post(`${API_BASE}/api/v1/control/device-registry/devices/${registryId}/fota`, data),
  updateFOTAProgress: (registryId, progress) => axios.put(`${API_BASE}/api/v1/control/device-registry/devices/${registryId}/fota/progress?progress=${progress}`),
  dispatchDevices: (data) => axios.post(`${API_BASE}/api/v1/control/device-registry/dispatch`, data),
  getDispatchHistory: (params) => axios.get(`${API_BASE}/api/v1/control/device-registry/dispatches`, { params }),
  getClientsForDispatch: () => axios.get(`${API_BASE}/api/v1/control/device-registry/clients`),

  // Device Assignment + QR Generation (Flow 1)
  assignDeviceToTenant: (registryId, data) => axios.post(`${API_BASE}/api/v1/control/device-registry/devices/${registryId}/assign-tenant`, data),
  getDeviceQR: (registryId) => axios.get(`${API_BASE}/api/v1/control/device-registry/devices/${registryId}/qr`),
  generatePassportQRBook: (data) => axios.post(`${API_BASE}/api/v1/control/device-registry/passport-qr-book`, data),
  shipToTenant: (data) => axios.post(`${API_BASE}/api/v1/control/device-registry/ship-to-tenant`, data),

  // Onboarding Wizard (5-step)
  receiveDevice: (data) => axios.post(`${API_BASE}/api/v1/control/onboarding/step1-receive-device`, data),
  registerClient: (data) => axios.post(`${API_BASE}/api/v1/control/onboarding/step2-register-client`, data),
  getOnboardingClients: () => axios.get(`${API_BASE}/api/v1/control/onboarding/clients`),
  getUnassignedDevices: () => axios.get(`${API_BASE}/api/v1/control/onboarding/unassigned-devices`),
  assignDeviceStep3: (data) => axios.post(`${API_BASE}/api/v1/control/onboarding/step3-assign-device`, data),
  generatePassportBookStep4: (data) => axios.post(`${API_BASE}/api/v1/control/onboarding/step4-passport-qr-book`, data),
  dispatchStep5: (data) => axios.post(`${API_BASE}/api/v1/control/onboarding/step5-dispatch`, data),
  getColdChainBreaches: (params) => axios.get(`${API_BASE}/api/v1/control/cold-chain/breaches`, { params }),
  getColdChainTrends: (params) => axios.get(`${API_BASE}/api/v1/control/cold-chain/trends`, { params }),
  getColdChainCompliance: (params) => axios.get(`${API_BASE}/api/v1/control/cold-chain/compliance-report`, { params }),
  getColdChainExcursions: (params) => axios.get(`${API_BASE}/api/v1/control/cold-chain/excursion-report`, { params }),
  getColdChainLiveMap: (params) => axios.get(`${API_BASE}/api/v1/control/cold-chain/live-map`, { params }),

  // Audit
  getAuditLogs: (tenantId, params) => axios.get(`${API_BASE}/api/v1/control/tenants/${tenantId}/audit`, { params }),

  // Integrations
  getIntegrationSummary: (tenantId) => axios.get(`${API_BASE}/api/v1/control/tenants/${tenantId}/integrations/summary`),
  getIntegrationClients: (tenantId, params) => axios.get(`${API_BASE}/api/v1/control/tenants/${tenantId}/integrations/clients`, { params }),
  updateIntegrationClient: (tenantId, clientId, data) => axios.patch(`${API_BASE}/api/v1/control/tenants/${tenantId}/integrations/clients/${clientId}`, data),
  getWebhooks: (tenantId, params) => axios.get(`${API_BASE}/api/v1/control/tenants/${tenantId}/integrations/webhooks`, { params }),
  updateWebhook: (tenantId, webhookId, data) => axios.patch(`${API_BASE}/api/v1/control/tenants/${tenantId}/integrations/webhooks/${webhookId}`, data),
  getWebhookDeliveries: (tenantId, webhookId, params) => axios.get(`${API_BASE}/api/v1/control/tenants/${tenantId}/integrations/webhooks/${webhookId}/deliveries`, { params }),

  // Billing & Licensing
  getBillingSummary: (tenantId) => axios.get(`${API_BASE}/api/v1/control/tenants/${tenantId}/billing/summary`),
  getBillingDashboard: (tenantId) => axios.get(`${API_BASE}/api/v1/control/tenants/${tenantId}/billing`),
  getLicenses: (tenantId, params) => axios.get(`${API_BASE}/api/v1/control/tenants/${tenantId}/billing/licenses`, { params }),
  extendLicense: (tenantId, licenseId, data) => axios.post(`${API_BASE}/api/v1/control/tenants/${tenantId}/billing/licenses/${licenseId}/extend`, data),
  suspendLicense: (tenantId, licenseId) => axios.post(`${API_BASE}/api/v1/control/tenants/${tenantId}/billing/licenses/${licenseId}/suspend`),
  reactivateLicense: (tenantId, licenseId) => axios.post(`${API_BASE}/api/v1/control/tenants/${tenantId}/billing/licenses/${licenseId}/reactivate`),

  // Global Audit
  getGlobalAuditLogs: (params) => axios.get(`${API_BASE}/api/v1/control/audit`, { params }),
  getGlobalAuditStats: (params) => axios.get(`${API_BASE}/api/v1/control/audit/stats`, { params }),
  getAuditEntityTypes: () => axios.get(`${API_BASE}/api/v1/control/audit/entity-types`),
  getAuditActions: () => axios.get(`${API_BASE}/api/v1/control/audit/actions`),

  // Global Devices
  getGlobalDeviceStats: () => axios.get(`${API_BASE}/api/v1/control/devices/stats`),
  getGlobalDevices: (params) => axios.get(`${API_BASE}/api/v1/control/devices`, { params }),
  createDevice: (data) => axios.post(`${API_BASE}/api/v1/control/devices`, data),
  getGlobalDeviceDetail: (deviceId) => axios.get(`${API_BASE}/api/v1/control/devices/${deviceId}`),
  getDeviceFeatures: (deviceId) => axios.get(`${API_BASE}/api/v1/control/devices/${deviceId}/features`),
  assignDevice: (deviceId, data) => axios.post(`${API_BASE}/api/v1/control/devices/${deviceId}/assign`, data),
  activateDevice: (deviceId, data) => axios.post(`${API_BASE}/api/v1/control/devices/${deviceId}/activate`, data),
  suspendGlobalDevice: (deviceId) => axios.post(`${API_BASE}/api/v1/control/devices/${deviceId}/suspend`),
  resumeGlobalDevice: (deviceId) => axios.post(`${API_BASE}/api/v1/control/devices/${deviceId}/resume`),
  deactivateDevice: (deviceId) => axios.post(`${API_BASE}/api/v1/control/devices/${deviceId}/deactivate`),
  bulkDeviceAction: (data) => axios.post(`${API_BASE}/api/v1/control/devices/bulk-action`, data),

  // Subscription Plans
  getPlans: (params) => axios.get(`${API_BASE}/api/v1/control/plans`, { params }),
  getPlan: (planId) => axios.get(`${API_BASE}/api/v1/control/plans/${planId}`),
  createPlan: (data) => axios.post(`${API_BASE}/api/v1/control/plans`, data),
  updatePlan: (planId, data) => axios.patch(`${API_BASE}/api/v1/control/plans/${planId}`, data),
  deletePlan: (planId) => axios.delete(`${API_BASE}/api/v1/control/plans/${planId}`),

  // Global Billing
  getGlobalBillingSummary: () => axios.get(`${API_BASE}/api/v1/control/billing/summary`),
  getGlobalInvoices: () => axios.get(`${API_BASE}/api/v1/control/billing/invoices`),
  getGlobalSubscriptions: () => axios.get(`${API_BASE}/api/v1/control/billing/subscriptions`),

  // Platform Settings
  getSettings: () => axios.get(`${API_BASE}/api/v1/control/settings`),
  updateSettings: (data) => axios.put(`${API_BASE}/api/v1/control/settings`, data),
  getAlertSettings: () => axios.get(`${API_BASE}/api/v1/control/settings/alerts`),
  getSecuritySettings: () => axios.get(`${API_BASE}/api/v1/control/settings/security`),
  getIntegrationSettings: () => axios.get(`${API_BASE}/api/v1/control/settings/integration`),

  // Platform Users (Track0tic internal staff)
  getPlatformUsers: () => axios.get(`${API_BASE}/api/v1/control/platform-users`),
  createPlatformUser: (data) => axios.post(`${API_BASE}/api/v1/control/platform-users`, data),
  updatePlatformUser: (userId, data) => axios.put(`${API_BASE}/api/v1/control/platform-users/${userId}`, data),
  deactivatePlatformUser: (userId) => axios.delete(`${API_BASE}/api/v1/control/platform-users/${userId}`),

  // Tenant Telemetry Configuration (profile + channels)
  getTenantTelemetryConfig: (tenantId) => axios.get(`${API_BASE}/api/v1/control/tenants/${tenantId}/telemetry-config`),
  updateTenantTelemetryConfig: (tenantId, data) => axios.put(`${API_BASE}/api/v1/control/tenants/${tenantId}/telemetry-config`, data),

  // Printable QR PDFs
  // Device stickers — opened in a new tab so the browser PDF viewer handles it.
  printDeviceStickersUrl: () => `${API_BASE}/api/v1/control/print/device-stickers`,
  printPassportBookUrl: (batchId) => `${API_BASE}/api/v1/control/print/passport-book/${batchId}`,
};

// Auth helpers (cookie-based)
export const authApi = {
  setPassword: (current_password, new_password) =>
    axios.post(`${API_BASE}/api/v1/auth/set-password`, { current_password, new_password }),
  me: () => axios.get(`${API_BASE}/api/v1/auth/me`),
};

// Customer Ops Portal APIs
export const opsApi = {
  // Auth
  login: (email, password) => axios.post(`${API_BASE}/api/v1/auth/login`, { email, password }),
  logout: () => axios.post(`${API_BASE}/api/v1/auth/logout`),
  getMe: () => axios.get(`${API_BASE}/api/v1/auth/me`),

  // Feature Entitlements
  getTenantFeatures: () => axios.get(`${API_BASE}/api/v1/features/tenant`),
  getDeviceFeatures: (deviceId) => axios.get(`${API_BASE}/api/v1/features/device/${deviceId}`),

  // Reports
  getReportsSummary: (days = 7) => axios.get(`${API_BASE}/api/v1/reports/summary`, { params: { days } }),
  getTemperatureReports: (days = 7) => axios.get(`${API_BASE}/api/v1/reports/temperature`, { params: { days } }),
  getDeliveryReports: (days = 7) => axios.get(`${API_BASE}/api/v1/reports/delivery`, { params: { days } }),
  getDeviceReports: () => axios.get(`${API_BASE}/api/v1/reports/devices`),
  getAlertReports: (days = 7) => axios.get(`${API_BASE}/api/v1/reports/alerts`, { params: { days } }),

  // Report Exports — cookie auth on the browser-initiated download.
  // window.open in a same-site context sends the HTTPOnly cookie automatically.
  exportReportCSV: (reportType, days = 7) => {
    window.open(`${API_BASE}/api/v1/reports/export/csv/${reportType}?days=${days}`, '_blank');
  },
  exportReportPDF: (reportType, days = 7) => {
    window.open(`${API_BASE}/api/v1/reports/export/pdf/${reportType}?days=${days}`, '_blank');
  },
  
  // Raw telemetry_logs for the current tenant (paginated).
  getTelemetryLogs: (params) => axios.get(`${API_BASE}/api/v1/ops/telemetry/logs`, { params }),

  // Historical data for charts
  getTemperatureHistory: (days = 7) => axios.get(`${API_BASE}/api/v1/reports/temperature/history`, { params: { days } }),
  getAlertHistory: (days = 7) => axios.get(`${API_BASE}/api/v1/reports/alerts/history`, { params: { days } }),
  getDeviceUptime: (days = 7) => axios.get(`${API_BASE}/api/v1/reports/devices/uptime`, { params: { days } }),

  // Locations (scoped)
  getLocations: (params) => axios.get(`${API_BASE}/api/v1/locations`, { params }),
  getLocationTree: () => axios.get(`${API_BASE}/api/v1/locations/tree`),
  
  // Users (tenant-scoped)
  getUsers: () => axios.get(`${API_BASE}/api/v1/users/`),
  getAssignableRoles: () => axios.get(`${API_BASE}/api/v1/users/assignable-roles`),
  createUser: (data) => axios.post(`${API_BASE}/api/v1/users/`, data),
  updateUser: (userId, data) => axios.put(`${API_BASE}/api/v1/users/${userId}`, data),
  deactivateUser: (userId) => axios.delete(`${API_BASE}/api/v1/users/${userId}`),

  // Work Orders
  getWorkOrders: (params) => axios.get(`${API_BASE}/api/v1/work-orders`, { params }),
  createWorkOrder: (data) => axios.post(`${API_BASE}/api/v1/work-orders`, data),
  getWorkOrder: (id) => axios.get(`${API_BASE}/api/v1/work-orders/${id}`),
  updateWorkOrder: (id, data) => axios.put(`${API_BASE}/api/v1/work-orders/${id}`, data),
  assignWorkOrder: (id, data) => axios.post(`${API_BASE}/api/v1/work-orders/${id}/assign`, data),
  startWorkOrder: (id) => axios.post(`${API_BASE}/api/v1/work-orders/${id}/start`),
  completeWorkOrder: (id) => axios.post(`${API_BASE}/api/v1/work-orders/${id}/complete`),
  cancelWorkOrder: (id) => axios.post(`${API_BASE}/api/v1/work-orders/${id}/cancel`),

  // Tracking & Telemetry
  getLiveTracking: (params) => axios.get(`${API_BASE}/api/v1/tracking/live`, { params }),
  getTelemetry: (params) => axios.get(`${API_BASE}/api/v1/telemetry`, { params }),
  getDeviceHealth: (params) => axios.get(`${API_BASE}/api/v1/devices/health`, { params }),
  getDeviceTelemetry: (deviceId, params) => axios.get(`${API_BASE}/api/v1/telemetry/device/${deviceId}`, { params }),

  // Live Telemetry Dashboard (channel-aware)
  getTelemetryDashboard: (hours = 24) => axios.get(`${API_BASE}/api/v1/ops/telemetry/dashboard`, { params: { hours } }),
  getTenantProfile: () => axios.get(`${API_BASE}/api/v1/ops/telemetry/profile`),

  // Printable Passport QR book (ops side — own tenant only)
  printPassportBookUrl: (batchId) => `${API_BASE}/api/v1/ops/print/passport-book/${batchId}`,

  // Alerts
  getAlerts: (params) => axios.get(`${API_BASE}/api/v1/alerts`, { params }),
  acknowledgeAlert: (id) => axios.post(`${API_BASE}/api/v1/alerts/${id}/acknowledge`),
  resolveAlert: (id) => axios.post(`${API_BASE}/api/v1/alerts/${id}/resolve`),

  // Reports
  getTripSummary: (workOrderId) => axios.get(`${API_BASE}/api/v1/reports/work-order/${workOrderId}/trip-summary`),
  getTemperatureCompliance: (workOrderId) => axios.get(`${API_BASE}/api/v1/reports/work-order/${workOrderId}/temperature-compliance`),
  exportReport: (workOrderId, format) => axios.get(`${API_BASE}/api/v1/reports/work-order/${workOrderId}/export`, { 
    params: { format },
    responseType: format === 'csv' ? 'blob' : 'json'
  }),

  // Trips & Invoice Reports
  getTrips: (params) => axios.get(`${API_BASE}/api/v1/ops/trips/`, { params }),
  getTrip: (tripId) => axios.get(`${API_BASE}/api/v1/ops/trips/${tripId}`),
  createTrip: (data) => axios.post(`${API_BASE}/api/v1/ops/trips`, data),
  startTrip: (tripId) => axios.post(`${API_BASE}/api/v1/ops/trips/${tripId}/start`),
  completeTrip: (tripId) => axios.post(`${API_BASE}/api/v1/ops/trips/${tripId}/complete`),
  getInvoiceReports: (params) => axios.get(`${API_BASE}/api/v1/invoice-reports/`, { params }),
  getInvoiceReport: (reportId) => axios.get(`${API_BASE}/api/v1/invoice-reports/${reportId}`),
  getInvoiceTelemetry: (tripInvoiceId) => axios.get(`${API_BASE}/api/v1/invoice-reports/telemetry/${tripInvoiceId}`),
  generateInvoiceReport: (tripInvoiceId) => axios.post(`${API_BASE}/api/v1/invoice-reports/generate/${tripInvoiceId}`),
  downloadInvoiceReportPDF: (reportId) => {
    // Cookie auth carries the session — no token in URL.
    return `${API_BASE}/api/v1/invoice-reports/${reportId}/pdf`;
  },
  verifyInvoiceReport: (token) => axios.get(`${API_BASE}/api/v1/invoice-reports/verify/${token}`),

  // Cold Chain Monitoring
  getColdChainDashboard: () => axios.get(`${API_BASE}/api/v1/ops/cold-chain/dashboard`),
  getColdChainLive: () => axios.get(`${API_BASE}/api/v1/ops/cold-chain/live`),
  getColdChainTrends: (params) => axios.get(`${API_BASE}/api/v1/ops/cold-chain/trends`, { params }),
  getColdChainTrip: (tripId) => axios.get(`${API_BASE}/api/v1/ops/cold-chain/trip/${tripId}`),

  // QR Notifications (Invoice-wise)
  getTripInvoicesForQR: (tripId) => axios.get(`${API_BASE}/api/v1/ops/qr-notify/trip/${tripId}/invoices`),
  sendQRNotification: (data) => axios.post(`${API_BASE}/api/v1/ops/qr-notify/send`, data),
  getQRNotificationHistory: (invoiceId) => axios.get(`${API_BASE}/api/v1/ops/qr-notify/history/${invoiceId}`),

  // Delivery Confirmation (OTP + QR)
  generateDeliveryOTP: (data) => axios.post(`${API_BASE}/api/v1/ops/delivery-confirm/generate-otp`, data),
  verifyDeliveryOTP: (data) => axios.post(`${API_BASE}/api/v1/ops/delivery-confirm/verify-otp`, data),
  getDeliveryConfirmStatus: (tripInvoiceId) => axios.get(`${API_BASE}/api/v1/ops/delivery-confirm/status/${tripInvoiceId}`),

  // Tenant Workflow - Labels
  getLabelStats: () => axios.get(`${API_BASE}/api/v1/ops/tenant-workflow/labels/stats`),
  getLabelBatches: () => axios.get(`${API_BASE}/api/v1/ops/tenant-workflow/labels/batches`),
  createLabelBatch: (data) => axios.post(`${API_BASE}/api/v1/ops/tenant-workflow/labels/batches`, data),
  getAvailableLabels: (limit) => axios.get(`${API_BASE}/api/v1/ops/tenant-workflow/labels/available`, { params: { limit } }),

  // Tenant Workflow - Dispatcher
  getReadyDevices: () => axios.get(`${API_BASE}/api/v1/ops/tenant-workflow/dispatcher/ready-devices`),
  dispatcherActivate: (data) => axios.post(`${API_BASE}/api/v1/ops/tenant-workflow/dispatcher/activate`, data),

  // Tenant Workflow - Passports
  listPassports: (params) => axios.get(`${API_BASE}/api/v1/ops/tenant-workflow/passports`, { params }),
  getPassportDetail: (id) => axios.get(`${API_BASE}/api/v1/ops/tenant-workflow/passports/${id}`),

  // Tenant Workflow - HUB Timeslice
  listHubTimeslices: (params) => axios.get(`${API_BASE}/api/v1/ops/tenant-workflow/hub/timeslices`, { params }),
  hubCheckIn: (data) => axios.post(`${API_BASE}/api/v1/ops/tenant-workflow/hub/check-in`, data),
  hubCheckOut: (data) => axios.post(`${API_BASE}/api/v1/ops/tenant-workflow/hub/check-out`, data),

  // Tenant Workflow - Delivery Flow
  getMyDeliveryTrips: () => axios.get(`${API_BASE}/api/v1/ops/tenant-workflow/delivery/my-trips`),
  getAllDeliveryTrips: () => axios.get(`${API_BASE}/api/v1/ops/tenant-workflow/delivery/all-trips`),
  scanAtStop: (data) => axios.post(`${API_BASE}/api/v1/ops/tenant-workflow/delivery/scan-at-stop`, data),
  markDelivered: (data) => axios.post(`${API_BASE}/api/v1/ops/tenant-workflow/delivery/mark-delivered`, data),
  handoffToTenant: (data) => axios.post(`${API_BASE}/api/v1/ops/tenant-workflow/delivery/handoff`, data),
  returnDevice: (data) => axios.post(`${API_BASE}/api/v1/ops/tenant-workflow/delivery/device-return`, data),

  // Phase 36a — CIT portal (Wave 1)
  getCitVendors: (params) => axios.get(`${API_BASE}/api/v1/ops/cit-vendors`, { params }),
  getCitVendor: (id) => axios.get(`${API_BASE}/api/v1/ops/cit-vendors/${id}`),
  createCitVendor: (data) => axios.post(`${API_BASE}/api/v1/ops/cit-vendors`, data),
  updateCitVendor: (id, data) => axios.patch(`${API_BASE}/api/v1/ops/cit-vendors/${id}`, data),
  approveCitVendor: (id) => axios.post(`${API_BASE}/api/v1/ops/cit-vendors/${id}/approve`),

  getVans: (params) => axios.get(`${API_BASE}/api/v1/ops/vans`, { params }),
  getVan: (id) => axios.get(`${API_BASE}/api/v1/ops/vans/${id}`),
  createVan: (data) => axios.post(`${API_BASE}/api/v1/ops/vans`, data),
  updateVan: (id, data) => axios.patch(`${API_BASE}/api/v1/ops/vans/${id}`, data),
  importVansCsv: (data) => axios.post(`${API_BASE}/api/v1/ops/vans/import`, data),

  getCitDrivers: (params) => axios.get(`${API_BASE}/api/v1/ops/drivers`, { params }),
  getCitDriver: (id) => axios.get(`${API_BASE}/api/v1/ops/drivers/${id}`),
  createCitDriver: (data) => {
    const isForm = typeof FormData !== 'undefined' && data instanceof FormData;
    return axios.post(`${API_BASE}/api/v1/ops/drivers`, data,
      isForm ? { headers: { 'Content-Type': 'multipart/form-data' } } : undefined);
  },
  updateCitDriver: (id, data) => {
    const isForm = typeof FormData !== 'undefined' && data instanceof FormData;
    if (isForm) {
      data.append('_method', 'PATCH');
      return axios.post(`${API_BASE}/api/v1/ops/drivers/${id}`, data,
        { headers: { 'Content-Type': 'multipart/form-data' } });
    }
    return axios.patch(`${API_BASE}/api/v1/ops/drivers/${id}`, data);
  },
  submitCitDriverKyc: (id, data) => axios.post(`${API_BASE}/api/v1/ops/drivers/${id}/submit-kyc`, data),
  approveCitDriverKyc: (id) => axios.post(`${API_BASE}/api/v1/ops/drivers/${id}/approve-kyc`),
  rejectCitDriverKyc: (id, data) => axios.post(`${API_BASE}/api/v1/ops/drivers/${id}/reject-kyc`, data),
  importCitDriversCsv: (data) => axios.post(`${API_BASE}/api/v1/ops/drivers/import`, data),

  getCitDashboard: (params) => axios.get(`${API_BASE}/api/v1/ops/cit/dashboard`, { params }),
};

// Telemetry Ingestion API (for testing/admin)
export const ingestApi = {
  sendTelemetry: (data) => axios.post(`${API_BASE}/api/v1/ingest/v1/telemetry`, data),
  getDeviceStatus: (deviceUid) => axios.get(`${API_BASE}/api/v1/ingest/v1/device/${deviceUid}/status`),
};

export default { controlApi, opsApi, ingestApi };

// Public APIs (no auth needed)
export const publicApi = {
  getDeliveryConfirmData: (token) => axios.get(`${API_BASE}/api/v1/ops/delivery-confirm/public/${token}`),
  confirmDeliveryPublic: (token, data) => axios.post(`${API_BASE}/api/v1/ops/delivery-confirm/public/${token}/confirm`, data),
};
